import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Upload, Trash2, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SectionBlock } from "./Channel";

const STATUS_META = {
  pending:  { label: "Pending review", color: "orange", Icon: Clock },
  approved: { label: "Approved",       color: "volt",   Icon: CheckCircle2 },
  rejected: { label: "Rejected",       color: "pink",   Icon: XCircle },
};

const TIERS = [
  { key: "free",       label: "Everyone" },
  { key: "sub",        label: "Subscribers (tier 1)" },
  { key: "sub_tier_2", label: "Tier 2 subs" },
  { key: "sub_tier_3", label: "Tier 3 subs" },
];

const CODE_RX = /^[a-zA-Z0-9_]{3,24}$/;

export default function Emotes() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [code, setCode] = useState("");
  const [tier, setTier] = useState("free");
  const [uploading, setUploading] = useState(false);
  const [emotes, setEmotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/emotes/custom/mine").then((r) => setEmotes(r.data.emotes || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const chooseFile = (f) => {
    if (!f) return;
    if (f.size > 1_048_576) return toast.error("File must be ≤ 1 MB");
    if (!/^image\/(png|jpeg|gif|webp)$/.test(f.type)) return toast.error("PNG, JPG, GIF, or WEBP only");
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const upload = async () => {
    if (!file) return toast.error("Choose an image first");
    if (!CODE_RX.test(code)) return toast.error("Code must be 3–24 letters, digits, or underscores");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("code", code);
    fd.append("tier", tier);
    setUploading(true);
    try {
      await api.post("/emotes/custom/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`:${code}: submitted for review`);
      setFile(null); setPreview(null); setCode(""); setTier("free");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this emote?")) return;
    try {
      await api.delete(`/emotes/custom/${id}`);
      toast.success("Emote removed");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="section-emotes">
      <SectionBlock icon={Upload} title="Upload New Emote" color="pink">
        <p className="text-xs text-zinc-400 -mt-1">
          128×128 recommended · PNG/JPG/GIF/WEBP · max 1 MB · code becomes <span className="sc-mono text-[color:var(--sc-cyan)]">:yourcode:</span> in chat.
        </p>
        <div className="grid md:grid-cols-[160px_1fr] gap-4 items-start">
          <label
            className="w-40 h-40 rounded border-2 border-dashed border-white/10 hover:border-[color:var(--sc-pink)]/60 grid place-items-center cursor-pointer overflow-hidden bg-black/20"
            data-testid="emotes-upload-dropzone"
          >
            {preview ? (
              <img src={preview} alt="preview" className="w-full h-full object-contain" data-testid="emotes-upload-preview" />
            ) : (
              <div className="text-center px-3">
                <Upload className="w-6 h-6 mx-auto mb-1 text-zinc-600" />
                <div className="text-[10px] sc-mono text-zinc-500">CHOOSE IMAGE</div>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              className="hidden"
              onChange={(e) => chooseFile(e.target.files?.[0])}
              data-testid="emotes-file-input"
            />
          </label>

          <div className="space-y-3 min-w-0">
            <div>
              <div className="sc-label text-[10px] mb-1">Emote code</div>
              <div className="flex items-center gap-2">
                <span className="sc-mono text-zinc-500">:</span>
                <input
                  className="sc-input flex-1 sc-mono"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="yourcode"
                  maxLength={24}
                  data-testid="emotes-code-input"
                />
                <span className="sc-mono text-zinc-500">:</span>
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                3–24 characters, letters/digits/underscores only.
                {code && !CODE_RX.test(code) && <span className="text-[color:var(--sc-pink)] ml-2">Invalid format</span>}
              </div>
            </div>
            <div>
              <div className="sc-label text-[10px] mb-1">Who can use it</div>
              <select className="sc-input" value={tier} onChange={(e) => setTier(e.target.value)} data-testid="emotes-tier-select">
                {TIERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <button
              onClick={upload}
              disabled={uploading || !file || !CODE_RX.test(code)}
              className="sc-btn text-sm flex items-center gap-2 disabled:opacity-50"
              data-testid="emotes-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Uploading…" : "Submit for review"}
            </button>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock icon={Sparkles} title="Your Emotes" color="volt">
        {loading ? (
          <div className="text-center py-6"><Loader2 className="w-5 h-5 mx-auto animate-spin text-zinc-500" /></div>
        ) : emotes.length === 0 ? (
          <div className="text-center text-xs text-zinc-500 py-8">You haven&apos;t uploaded any custom emotes yet.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="emotes-list">
            {emotes.map((e) => {
              const meta = STATUS_META[e.status] || STATUS_META.pending;
              const Icon = meta.Icon;
              return (
                <div key={e.id} className="term-card p-3" data-testid={`emote-${e.code}`}>
                  <div className="aspect-square rounded bg-black/30 grid place-items-center overflow-hidden mb-2">
                    <img src={e.url} alt={e.code} className="w-full h-full object-contain" />
                  </div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="sc-mono text-xs font-bold truncate">:{e.code}:</div>
                    <button
                      onClick={() => remove(e.id)}
                      className="p-1 rounded hover:bg-white/5 text-zinc-500 hover:text-[color:var(--sc-pink)]"
                      data-testid={`emote-delete-${e.code}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] sc-mono" style={{ color: `var(--sc-${meta.color})` }}>
                    <Icon className="w-3 h-3" />
                    {meta.label}
                  </div>
                  {e.status === "rejected" && e.rejection_reason && (
                    <div className="text-[10px] text-zinc-500 mt-1 truncate" title={e.rejection_reason}>
                      Reason: {e.rejection_reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionBlock>
    </div>
  );
}
